import { Building, FileText, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";
import ServicePageLayout from "../components/ServicePageLayout";

const prestations = [
    {
        icon: Building,
        title: "Choix de la structure juridique",
        desc: "Conseil personnalisé pour choisir entre SA, Sàrl, succursale ou holding, selon votre activité, vos actionnaires et vos objectifs fiscaux.",
    },
    {
        icon: FileText,
        title: "Immatriculation au registre du commerce",
        desc: "Rédaction des statuts, préparation des actes notariés et suivi complet du dossier jusqu'à l'inscription au registre du commerce de Genève.",
    },
    {
        icon: CheckCircle,
        title: "Accompagnement post-création",
        desc: "Mise en place de la comptabilité, immatriculation TVA si nécessaire, annonce AVS et ouverture du compte bancaire professionnel.",
    },
];

const etapes = [
    {
        num: "01",
        title: "Définition du projet",
        desc: "Entretien pour définir votre projet et choisir la forme juridique adaptée.",
    },
    {
        num: "02",
        title: "Rédaction et notariat",
        desc: "Rédaction des statuts, démarches notariées et dépôt du capital.",
    },
    {
        num: "03",
        title: "Immatriculation et gestion",
        desc: "Inscription au registre du commerce et mise en place de la gestion courante.",
    },
];

const pourquoiCols = [
    {
        title: "Les bons choix dès le début",
        desc: (
            <>
                La forme juridique choisie à la création conditionne votre{" "}
                <Link
                    to="/fiscalite"
                    data-testid="link-fiscalite"
                    className="text-[#1C9CAC] hover:underline"
                >
                    fiscalité
                </Link>{" "}
                pour des années. Fiduciaire Bleich vous aide à faire le bon choix avant toute démarche.
            </>
        ),
    },
    {
        title: "Tout en un seul endroit",
        desc: (
            <>
                Après la création, Fiduciaire Bleich prend en charge la{" "}
                <Link
                    to="/comptabilite"
                    data-testid="link-comptabilite"
                    className="text-[#1C9CAC] hover:underline"
                >
                    comptabilité
                </Link>
                , les{" "}
                <Link
                    to="/salaires-rh"
                    data-testid="link-salaires-rh"
                    className="text-[#1C9CAC] hover:underline"
                >
                    salaires
                </Link>{" "}
                et la{" "}
                <Link
                    to="/fiscalite"
                    data-testid="link-fiscalite-2"
                    className="text-[#1C9CAC] hover:underline"
                >
                    fiscalité
                </Link>{" "}
                de votre nouvelle société. Un seul interlocuteur pour tout.
            </>
        ),
    },
    {
        title: "Genève et international",
        desc: "Fiduciaire Bleich accompagne aussi bien les entrepreneurs genevois que les investisseurs étrangers souhaitant établir une présence en Suisse.",
    },
];

const faqs = [
    {
        q: "Quel capital minimum pour créer une SA ou une Sàrl en Suisse ?",
        a: "Une SA requiert un capital-actions de CHF 100'000, dont au moins CHF 50'000 doivent être libérés à la constitution. Une Sàrl requiert un capital social de CHF 20'000, entièrement libéré. Fiduciaire Bleich vous guide selon votre projet.",
    },
    {
        q: "SA ou Sàrl, que choisir ?",
        a: "Le choix dépend du capital disponible, du nombre d'associés, de la visibilité de l'actionnariat et de vos objectifs. Fiduciaire Bleich compare les options avec vous avant toute démarche.",
    },
    {
        q: "Combien de temps faut-il pour créer une société à Genève ?",
        a: "Le délai dépend de la préparation du dossier et des étapes notariées et administratives. Fiduciaire Bleich organise les démarches pour avancer sans perte de temps.",
    },
    {
        q: "Un investisseur étranger peut-il créer une société en Suisse ?",
        a: (
            <>
                Oui, sous réserve de désigner une{" "}
                <Link
                    to="/administrateur-gerant"
                    data-testid="link-administrateur-gerant"
                    className="text-[#1C9CAC] hover:underline"
                >
                    représentation locale
                </Link>{" "}
                lorsque la loi l'exige. Fiduciaire Bleich peut assurer ce rôle.
            </>
        ),
    },
];

// JSON-LD FAQ uses plain text answers
const faqsPlain = [
    {
        q: "Quel capital minimum pour créer une SA ou une Sàrl en Suisse ?",
        a: "Une SA requiert un capital-actions de CHF 100'000, dont au moins CHF 50'000 doivent être libérés à la constitution. Une Sàrl requiert un capital social de CHF 20'000, entièrement libéré. Fiduciaire Bleich vous guide selon votre projet.",
    },
    {
        q: "SA ou Sàrl, que choisir ?",
        a: "Le choix dépend du capital disponible, du nombre d'associés, de la visibilité de l'actionnariat et de vos objectifs. Fiduciaire Bleich compare les options avec vous avant toute démarche.",
    },
    {
        q: "Combien de temps faut-il pour créer une société à Genève ?",
        a: "Le délai dépend de la préparation du dossier et des étapes notariées et administratives. Fiduciaire Bleich organise les démarches pour avancer sans perte de temps.",
    },
    {
        q: "Un investisseur étranger peut-il créer une société en Suisse ?",
        a: "Oui, sous réserve de désigner une représentation locale lorsque la loi l'exige. Fiduciaire Bleich peut assurer ce rôle.",
    },
];

const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    name: "Création de société à Genève",
    serviceType: "Conseil et accompagnement à la création de société, SA, Sàrl, holding",
    provider: {
        "@type": "AccountingService",
        name: "Fiduciaire Bleich Sàrl",
        url: "https://fiduciaire-bleich.ch",
    },
    areaServed: { "@type": "AdministrativeArea", name: "Canton de Genève" },
    url: "https://fiduciaire-bleich.ch/creation-societe",
};

const breadcrumbJsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
        { "@type": "ListItem", position: 1, name: "Accueil", item: "https://fiduciaire-bleich.ch/" },
        { "@type": "ListItem", position: 2, name: "Nos services", item: "https://fiduciaire-bleich.ch/" },
        { "@type": "ListItem", position: 3, name: "Création de société", item: "https://fiduciaire-bleich.ch/creation-societe" },
    ],
};

const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqsPlain.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
};

const CreationSociete = () => {
    return (
        <ServicePageLayout
            testIdPrefix="creation-societe"
            seo={{
                title: "Création de société à Genève | Fiduciaire Bleich",
                description:
                    "Création de SA, Sàrl et holdings à Genève. Conseil sur la structure juridique et accompagnement administratif complet. Fiduciaire Bleich depuis 2004.",
                canonical: "https://fiduciaire-bleich.ch/creation-societe",
            }}
            jsonLd={{ service: serviceJsonLd, breadcrumb: breadcrumbJsonLd, faq: faqJsonLd }}
            breadcrumb="Création de société"
            hero={{
                eyebrow: "Nos services",
                h1Prefix: "Création de société à",
                h1Italic: "Genève.",
                subtitle: "De l'idée à l'immatriculation, accompagnés à chaque étape.",
                intro:
                    "Créer une société en Suisse demande de faire les bons choix dès le départ : forme juridique, structure du capital, siège, gouvernance. Fiduciaire Bleich vous conseille et prend en charge l'ensemble des démarches administratives jusqu'à l'immatriculation au registre du commerce.",
            }}
            prestations={prestations}
            prestationsHeading={
                <>
                    Ce que <span className="italic">nous faisons.</span>
                </>
            }
            etapes={etapes}
            processHeading={
                <>
                    De l'idée à <span className="italic">l'immatriculation.</span>
                </>
            }
            pourquoiHeading={
                <>
                    Un accompagnement complet,
                    <br />
                    de la <span className="italic">création à la gestion courante.</span>
                </>
            }
            pourquoiCols={pourquoiCols}
            faqHeading={
                <>
                    Questions fréquentes
                    <br />
                    sur la <span className="italic">création de société.</span>
                </>
            }
            faqs={faqs}
            cta={{
                heading: (
                    <>
                        Parlons de votre <span className="italic">projet.</span>
                    </>
                ),
                text: "Un premier entretien gratuit et confidentiel permet de cadrer votre projet de création et l'accompagnement que Fiduciaire Bleich peut vous offrir. Sans engagement.",
            }}
        />
    );
};

export default CreationSociete;
